/**
 * AsHelpHover Component
 *
 * A reusable component that renders a "?" help icon with tooltip/hovercard functionality.
 * Can be used with either:
 * 1. A child <as-hovercard> element for rich content
 * 2. help-tooltip-default-text / help-tooltip-dict-key attributes for simple text tooltips
 *
 * Usage:
 * ```html
 * <!-- With attributes -->
 * <as-help-hover help-tooltip-default-text="This is help text"></as-help-hover>
 *
 * <!-- With hovercard -->
 * <as-help-hover>
 *   <as-hovercard>
 *     <p>Rich help content here</p>
 *   </as-hovercard>
 * </as-help-hover>
 * ```
 *
 * Attributes:
 * - help-tooltip-default-text: Default text for simple tooltip
 * - help-tooltip-dict-key: Localization key for tooltip text
 * - placement: Position of the tooltip/hovercard (default: 'right')
 */

import '../overlays/as-tooltip-wrapper.ts';
import '../overlays/as-hovercard-wrapper.ts';
import { AsHovercard } from '../overlays/as-hovercard.ts';
import styleText from './as-help-hover.css?raw';

export class AsHelpHover extends HTMLElement {
  static readonly tag = 'as-help-hover' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  // Get the hovercard stylesheet to include in shadow DOM
  private static readonly _hovercardSheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(`
      as-hovercard {
        display: block;
        position: relative;
        background: var(--hovercard-bg, white);
        border: var(--hovercard-border, 1px solid #ccc);
        border-radius: var(--hovercard-border-radius, 8px);
        box-shadow: var(--hovercard-box-shadow, 0 4px 12px rgba(0, 0, 0, 0.15));
        padding: var(--hovercard-padding, 0);
        max-width: var(--hovercard-max-width, none);
        min-width: var(--hovercard-min-width, none);
        box-sizing: border-box;
        font-family: inherit;
        line-height: 1.4;
      }

      as-hovercard .title {
        font-weight: 700;
        font-size: 1.1rem;
      }

      as-hovercard img {
        display: block;
        max-width: 100%;
        height: auto;
      }

      .as-hovercard-close {
        position: absolute;
        top: 0;
        right: 0;
        width: var(--hovercard-close-button-size, 24px);
        height: var(--hovercard-close-button-size, 24px);
        min-width: var(--hovercard-close-button-size, 24px);
        min-height: var(--hovercard-close-button-size, 24px);
        max-width: var(--hovercard-close-button-size, 24px);
        max-height: var(--hovercard-close-button-size, 24px);
        border: none;
        background: var(--hovercard-close-button-bg, rgba(0, 0, 0, 0.6));
        color: var(--hovercard-close-button-color, white);
        font-size: 14px;
        font-weight: normal;
        line-height: 1;
        font-family: Arial, sans-serif;
        cursor: pointer;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        z-index: 10;
        transform: translate(50%, -50%);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        box-sizing: border-box;
        padding: 0;
        aspect-ratio: 1 / 1;
      }

      .as-hovercard-close:hover {
        color: var(--hovercard-close-button-hover-color, white);
        background: var(--hovercard-close-button-hover-bg, rgba(0, 0, 0, 0.8));
        transform: translate(50%, -50%) scale(1.1);
      }

      .as-hovercard-close:focus {
        outline: 2px solid #007bff;
        outline-offset: 2px;
      }
    `);
    return sheet;
  })();

  static get observedAttributes(): string[] {
    return ['help-tooltip-default-text', 'help-tooltip-dict-key', 'placement'];
  }

  private _shadowRoot: ShadowRoot;
  private _hasRendered = false;

  constructor() {
    super();
    this._shadowRoot = this.attachShadow({ mode: 'open' });
    // Include both as-help-hover styles and as-hovercard styles
    this._shadowRoot.adoptedStyleSheets = [AsHelpHover._sheet, AsHelpHover._hovercardSheet];
  }

  connectedCallback(): void {
    if (!this._hasRendered) {
      this._render();
      this._hasRendered = true;
    }
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (this._hasRendered) {
      this._render();
    }
  }

  private _render(): void {
    const placement = this.getAttribute('placement') ?? 'right';
    const helpTooltipDefaultText = this.getAttribute('help-tooltip-default-text');
    const helpTooltipDictKey = this.getAttribute('help-tooltip-dict-key');

    // Check if there's a child as-hovercard in light DOM
    const hovercard = this.querySelector('as-hovercard');

    if (hovercard) {
      // Remove hovercard from light DOM and move it into shadow DOM
      hovercard.remove();

      // Create hovercard wrapper in shadow DOM
      this._shadowRoot.innerHTML = `
        <as-hovercard-wrapper placement="${placement}" show-on-hover>
          <span class="help-icon">?</span>
        </as-hovercard-wrapper>
      `;

      // Append the hovercard directly into the wrapper
      const wrapper = this._shadowRoot.querySelector('as-hovercard-wrapper');
      if (wrapper) {
        wrapper.appendChild(hovercard);
      }
    } else if (helpTooltipDictKey || helpTooltipDefaultText) {
      // Use tooltip wrapper for simple text
      const tooltipAttrs = [];
      if (helpTooltipDictKey) {
        tooltipAttrs.push(`dict-key="${helpTooltipDictKey}"`);
      }
      if (helpTooltipDefaultText) {
        tooltipAttrs.push(`default-text="${helpTooltipDefaultText}"`);
      }

      this._shadowRoot.innerHTML = `
        <as-tooltip-wrapper placement="${placement}" show-on-hover ${tooltipAttrs.join(' ')}>
          <span class="help-icon">?</span>
        </as-tooltip-wrapper>
      `;
    } else {
      // No help content provided
      this._shadowRoot.innerHTML = '';
    }
  }
}

customElements.define(AsHelpHover.tag, AsHelpHover);
